import * as THREE from 'three'
import { MOUSE, TOUCH } from 'three'
import { OrbitControls as ThreeOrbitControls } from 'three/addons/controls/OrbitControls.js'
import { CONTROL_DAMPING_FACTOR } from '@/shared/constants'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { CameraState } from '@/shared/types/core'
import type { RenderContributor } from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import {
  CONTROL_GRAPHICS_REQUIREMENTS,
  controlOverlayGraphicsAdapter,
} from './ControlOverlayGraphicsAdapter'
import {
  type NativeControlSurfacePickingOptions,
  resolveNativeControlMinZoomStep,
  resolveNativeControlPickWindowSize,
} from './NativeControlSurfacePickingOptions'
import {
  accumulateWheelInputDelta,
  DEFAULT_NAVIGATION_SCALE_ABSOLUTE_MINIMUM,
  DEFAULT_WHEEL_ABSOLUTE_MIN_STEP,
  DEFAULT_WHEEL_MIN_STEP_RATIO,
  isCurrentViewportCamera,
  isPointBeyondNativeControlPickPlane,
  NATIVE_CONTROL_SURFACE_KINDS,
  type NativeControlServices,
  type NativeControlSurfaceQueryHit,
  resolveDoubleClickTargetRadius,
  resolveDoubleClickWheelDelta,
  resolveNativeControlPickMinDistanceAtScreen,
  resolveNavigationScale,
  resolvePickedPointWheelStep,
  resolveWheelForwardStep,
  resolveWheelInputDelta,
  shouldUseAsyncPicking,
  syncRigFromCamera,
  syncViewportCameraFromRig,
} from './NativeControlUtils'

const KEYBOARD_TRANSLATION_CODES = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowLeft',
  'ArrowDown',
  'ArrowRight',
  'KeyQ',
  'KeyE',
])

/** EarthController 构造参数 */
export interface EarthControllerOptions {
  /** 控制器共享服务集合 */
  services: NativeControlServices
  /** 表面支点的拾取近平面偏移 */
  surfacePicking?: NativeControlSurfacePickingOptions
  /** EarthController 表面拾取窗口像素尺寸，默认 129。 */
  windowSize?: number
  /** EarthController 缩放绝对最小步长，单位为世界坐标，默认 0.3。 */
  minZoomStep?: number
}

/**
 * Legacy earth-style controller:
 * - left drag rotates around a picked pivot
 * - right drag pans
 * - wheel zooms toward the picked point under cursor when possible
 */
export class EarthController extends ThreeOrbitControls implements RenderContributor {
  private static readonly WORLD_UP = new THREE.Vector3(0, 0, 1)
  private static readonly LEGACY_ROTATION_SPEED = 10
  private static readonly LEGACY_WHEEL_FADE_FACTOR = 20
  private static readonly LEGACY_WHEEL_STEP_RATIO = 0.2
  private static readonly FREE_WHEEL_STEP_RATIO = 0.1
  private static readonly MAX_ABSOLUTE_PITCH = Math.PI / 2 - 1e-3
  private static readonly ACTIVATION_TRANSITION_MS = 180
  private static readonly UP_ALIGNMENT_EPSILON = 1e-3
  private static readonly PIVOT_INDICATOR_PIXEL_RADIUS = 10
  private static readonly GSPLAT_HOVER_RADIUS = 0.08
  private static readonly DEFAULT_FRAME_DELTA = 1 / 60
  private static readonly MIN_KEYBOARD_MAX_SPEED = 1
  private static readonly KEYBOARD_ACCELERATION_DURATION = 1.0
  private static readonly NAVIGATION_SCALE_ABSOLUTE_MINIMUM =
    DEFAULT_NAVIGATION_SCALE_ABSOLUTE_MINIMUM
  private static readonly WHEEL_MIN_STEP_RATIO = DEFAULT_WHEEL_MIN_STEP_RATIO
  private static readonly WHEEL_ABSOLUTE_MIN_STEP = DEFAULT_WHEEL_ABSOLUTE_MIN_STEP
  private static readonly DOUBLE_CLICK_ORTHOGRAPHIC_MIN_ZOOM_FACTOR = 2.5
  // A WebGPU pick readback can take longer than a frame on large splat sets.  Keep the world
  // pivot for the duration of a short wheel gesture instead of starting a second full GPU sort
  // for every wheel event at the same pointer location.
  private static readonly WHEEL_PICK_REUSE_MS = 120
  private static readonly WHEEL_GESTURE_IDLE_MS = 180
  private static readonly WHEEL_PICK_REUSE_DISTANCE_PX = 2

  /** 渲染贡献者类型标识 */
  readonly kind = 'earth-control-overlay'
  /** 通过 ControlPlugin 私有 adapter 提交默认 surface overlay。 */
  readonly graphicsRequirements = CONTROL_GRAPHICS_REQUIREMENTS
  /** 需要参与的渲染阶段 */
  readonly phases = [RenderPhase.Overlay3D] as const
  /** 渲染优先级 */
  readonly renderPriority = 0

  private services: NativeControlServices | null = null
  private readonly surfacePickNearOffset: number | undefined
  private readonly surfacePickWindowSize: number
  private readonly minZoomStep: number
  private boundPointerDown: ((event: PointerEvent) => void) | null = null
  private boundPointerMove: ((event: PointerEvent) => void) | null = null
  private boundPointerUp: ((event: PointerEvent) => void) | null = null
  private boundWheel: ((event: WheelEvent) => void) | null = null
  private boundDoubleClick: ((event: MouseEvent) => void) | null = null
  private boundKeyDown: ((event: KeyboardEvent) => void) | null = null
  private boundKeyUp: ((event: KeyboardEvent) => void) | null = null
  private boundBlur: (() => void) | null = null
  private activePivot: THREE.Vector3 | null = null
  private activePointerId: number | null = null
  private lastPointerScreen: THREE.Vector2 | null = null
  private activeDragMode: 'rotate' | 'pan' | null = null
  private lastStableYaw = 0
  private isActivating = false
  private activationToken = 0
  private activationPending = false
  private readonly pivotIndicator: THREE.Mesh<THREE.SphereGeometry, THREE.MeshNormalMaterial>
  private readonly overlayScene = new THREE.Scene()
  private wheelDelta = 0
  private readonly zoomDelta = new THREE.Vector3()
  private wheelPivot: THREE.Vector3 | null = null
  private wheelPivotPending = false
  private zoomDistance: number | null = null
  private orthographicZoomTarget: number | null = null
  private orthographicZoomAnchorClient: THREE.Vector2 | null = null
  private readonly keyState = new Set<string>()
  private keyboardMaxSpeed = EarthController.MIN_KEYBOARD_MAX_SPEED
  private keyboardCurrentSpeed = 0
  private lastWheelPickAt = -Infinity
  private lastWheelPickScreen: THREE.Vector2 | null = null
  private lastWheelPickedPivot: THREE.Vector3 | null = null
  private pickRequestVersion = 0
  private pickAbortController: AbortController | null = null
  private lastCommittedRigState: CameraState | null = null

  constructor(options: EarthControllerOptions) {
    const camera = syncViewportCameraFromRig(options.services)
    super(camera)

    this.services = options.services
    this.surfacePickNearOffset = options.surfacePicking?.nearOffset
    this.surfacePickWindowSize = resolveNativeControlPickWindowSize(
      options.windowSize,
      'EarthController.windowSize'
    )
    this.minZoomStep = resolveNativeControlMinZoomStep(
      options.minZoomStep,
      'EarthController.minZoomStep'
    )
    this.enabled = false
    this.enableDamping = true
    this.dampingFactor = CONTROL_DAMPING_FACTOR
    this.screenSpacePanning = false
    this.zoomToCursor = true
    this.mouseButtons = {
      LEFT: -1 as unknown as MOUSE,
      MIDDLE: MOUSE.DOLLY,
      RIGHT: -1 as unknown as MOUSE,
    }
    this.touches = {
      ONE: TOUCH.ROTATE,
      TWO: TOUCH.DOLLY_PAN,
    }
    this.target.copy(options.services.rig.target)
    this.cursor.copy(options.services.rig.target)

    this.pivotIndicator = new THREE.Mesh(
      new THREE.SphereGeometry(0.8, 16, 16),
      new THREE.MeshNormalMaterial()
    )
    this.pivotIndicator.name = 'earthPivotIndicator'
    this.pivotIndicator.visible = false
    this.overlayScene.add(this.pivotIndicator)
  }

  /**
   * 连接 DOM 元素，绑定指针/键盘事件并启动过渡动画。
   *
   * @param element - 承载交互事件的 HTML 元素
   */
  override connect(element: HTMLElement): void {
    if (!this.services) return

    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '0')
    }

    const camera = this.syncFromRig()
    this.target.copy(this.services.rig.target)
    this.cursor.copy(this.services.rig.target)
    this.resetKeyboardMotionProfile()
    this.rememberCurrentRigState()
    camera.updateMatrixWorld(true)
    super.connect(element)
    this.installPointerHooks(element)
    super.update()
    element.focus()
    this.activationPending = true
  }

  /**
   * 每帧更新控制器状态，同步相机与 rig。
   *
   * @param deltaTime - 自上一帧的时间间隔，单位秒
   * @returns true 表示发生了可见的相机变更；false 表示无变更
   */
  override update(deltaTime?: number | null): boolean {
    if (!this.enabled || !this.services) return false

    let camera = this.syncFromRig()
    this.resetKeyboardMotionProfileForExternalRigChange()
    this.target.copy(this.services.rig.target)
    if (this.activationPending) {
      this.activationPending = false
      void this.beginActivationTransition()
    }
    if (this.isActivating) {
      camera.updateMatrixWorld(true)
      this.updatePivotIndicatorScale(camera)
      return false
    }
    if (this.activeDragMode) {
      camera.updateMatrixWorld(true)
      this.updatePivotIndicatorScale(camera)
      return false
    }
    const orbitChanged = super.update(deltaTime)
    camera.updateMatrixWorld(true)
    syncRigFromCamera(this.services, camera, this.target)
    this.rememberCurrentRigState()
    const keyboardChanged = this.updateKeyboardTranslation(deltaTime)
    const wheelChanged =
      camera instanceof THREE.OrthographicCamera ? false : this.updateLegacyWheelZoom(deltaTime)
    const orthographicZoomChanged = this.updateOrthographicZoomAnimation(deltaTime)
    camera = this.syncFromRig()
    camera.updateMatrixWorld(true)
    this.updatePivotIndicatorScale(camera)
    return orbitChanged || keyboardChanged || wheelChanged || orthographicZoomChanged
  }

  /** 断开 DOM 连接，移除事件监听并隐藏枢轴指示器 */
  override disconnect(): void {
    this.cancelPendingPickRequests()
    this.removePointerHooks()
    this.hidePivotIndicator()
    if (this.domElement) {
      super.disconnect()
    }
  }

  /** 释放控制器资源，清理 overlay 场景与几何材质 */
  override dispose(): void {
    this.cancelPendingPickRequests()
    this.hidePivotIndicator()
    this.removePointerHooks()
    this.overlayScene.remove(this.pivotIndicator)
    this.pivotIndicator.geometry.dispose()
    this.pivotIndicator.material.dispose()
    if (this.domElement) {
      super.dispose()
    }
  }

  /**
   * 渲染枢轴指示器到当前视图。
   *
   * @param frame - 帧上下文，提供相机
   * @param _phase - 当前渲染阶段
   * @param ctx - 当前阶段渲染上下文
   */
  render(frame: ViewFrameContext, _phase: RenderPhase, ctx: PhaseRenderContext): void {
    if (
      !this.enabled ||
      !this.pivotIndicator.visible ||
      (this.services?.viewId != null && ctx.viewId !== this.services.viewId)
    ) {
      return
    }

    controlOverlayGraphicsAdapter.render(this.overlayScene, frame.camera, ctx)
  }

  private syncFromRig(): ReturnType<typeof syncViewportCameraFromRig> {
    if (!this.services) {
      return this.object as ReturnType<typeof syncViewportCameraFromRig>
    }

    const camera = syncViewportCameraFromRig(this.services)
    if (this.object !== camera) {
      this.object = camera
    }
    return camera
  }

  private installPointerHooks(element: HTMLElement): void {
    this.removePointerHooks()

    this.boundPointerDown = (event: PointerEvent) => {
      if (!this.enabled || this.isActivating || (event.button !== 0 && event.button !== 2)) {
        return
      }

      element.focus()
      this.activePointerId = event.pointerId
      this.lastPointerScreen = new THREE.Vector2(event.clientX, event.clientY)
      this.activeDragMode = event.button === 0 ? 'rotate' : 'pan'

      if (event.button === 0) {
        const pickedPivot = shouldUseAsyncPicking(this.services)
          ? null
          : this.pickPivot(event, 'move')
        const pivot = pickedPivot ?? this.getFallbackPivot()
        this.activePivot = pivot.clone()
        this.cursor.copy(pivot)
        if (pickedPivot) {
          this.showPivotIndicator(pivot)
        } else {
          this.hidePivotIndicator()
        }
        if (shouldUseAsyncPicking(this.services)) {
          this.schedulePointerPivotPick(event)
        }
      } else {
        this.activePivot = null
        this.hidePivotIndicator()
      }

      event.preventDefault()

      if (typeof element.setPointerCapture === 'function') {
        element.setPointerCapture(event.pointerId)
      }
    }

    this.boundPointerMove = (event: PointerEvent) => {
      if (!this.enabled) {
        return
      }

      if (event.pointerId !== this.activePointerId || !this.lastPointerScreen) {
        return
      }

      const current = new THREE.Vector2(event.clientX, event.clientY)
      const delta = current.clone().sub(this.lastPointerScreen)
      this.lastPointerScreen.copy(current)

      if (delta.lengthSq() === 0) {
        return
      }

      if (this.activeDragMode === 'rotate' && this.activePivot) {
        this.rotateAroundPivot(delta, this.activePivot)
      } else if (this.activeDragMode === 'pan') {
        this.panCamera(delta)
      }
      event.preventDefault()
    }

    this.boundPointerUp = (event: PointerEvent) => {
      if (event.pointerId !== this.activePointerId) {
        return
      }

      event.preventDefault()
      this.activePivot = null
      this.activePointerId = null
      this.lastPointerScreen = null
      this.activeDragMode = null
      this.hidePivotIndicator()

      if (typeof element.releasePointerCapture === 'function') {
        element.releasePointerCapture(event.pointerId)
      }
    }

    this.boundWheel = (event: WheelEvent) => {
      if (!this.enabled) {
        return
      }

      element.focus()
      const camera = this.syncFromRig()
      if (!(camera instanceof THREE.PerspectiveCamera)) {
        this.updatePivotFromEvent(event, 'move', false)
        return
      }

      const wheelStep = resolveWheelInputDelta(event.deltaY, event.deltaMode)
      if (wheelStep === 0) {
        return
      }

      const pivot = this.getWheelPivot(event)
      if (pivot) {
        this.cursor.copy(pivot)
        this.wheelPivot = pivot.clone()
      } else {
        this.wheelPivot = null
      }

      this.wheelDelta = accumulateWheelInputDelta(this.wheelDelta, wheelStep)
      event.preventDefault()
      event.stopImmediatePropagation()
    }

    this.boundDoubleClick = (event: MouseEvent) => {
      if (event.defaultPrevented) {
        return
      }

      if (!this.enabled) {
        return
      }

      element.focus()
      const camera = this.syncFromRig()
      if (shouldUseAsyncPicking(this.services)) {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.scheduleDoubleClickPick(event, camera)
        return
      }

      const hit = this.pickSurfaceHit(event, 'click')
      if (!hit) {
        return
      }
      this.applyDoubleClickHit(event, camera, hit)
    }

    this.boundKeyDown = (event: KeyboardEvent) => {
      if (
        !this.enabled ||
        this.isActivating ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        !KEYBOARD_TRANSLATION_CODES.has(event.code)
      ) {
        return
      }

      this.keyState.add(event.code)
      event.preventDefault()
    }

    this.boundKeyUp = (event: KeyboardEvent) => {
      if (!KEYBOARD_TRANSLATION_CODES.has(event.code)) {
        return
      }

      this.keyState.delete(event.code)
      event.preventDefault()
    }

    this.boundBlur = () => {
      this.keyState.clear()
      this.keyboardCurrentSpeed = 0
    }

    element.addEventListener('pointerdown', this.boundPointerDown as EventListener, {
      capture: true,
    })
    element.addEventListener('pointermove', this.boundPointerMove as EventListener, {
      capture: true,
    })
    element.addEventListener('pointerup', this.boundPointerUp as EventListener, { capture: true })
    element.addEventListener('pointercancel', this.boundPointerUp as EventListener, {
      capture: true,
    })
    element.addEventListener('wheel', this.boundWheel as EventListener, { capture: true })
    element.addEventListener('dblclick', this.boundDoubleClick as EventListener)
    element.addEventListener('keydown', this.boundKeyDown as EventListener)
    element.addEventListener('keyup', this.boundKeyUp as EventListener)
    element.addEventListener('blur', this.boundBlur)
  }

  private removePointerHooks(): void {
    if (!this.domElement) {
      this.boundPointerDown = null
      this.boundPointerMove = null
      this.boundPointerUp = null
      this.boundWheel = null
      this.boundDoubleClick = null
      this.boundKeyDown = null
      this.boundKeyUp = null
      this.boundBlur = null
      this.keyState.clear()
      this.keyboardCurrentSpeed = 0
      return
    }

    if (this.boundPointerDown) {
      this.domElement.removeEventListener(
        'pointerdown',
        this.boundPointerDown as EventListener,
        true
      )
      this.boundPointerDown = null
    }

    if (this.boundPointerMove) {
      this.domElement.removeEventListener(
        'pointermove',
        this.boundPointerMove as EventListener,
        true
      )
      this.boundPointerMove = null
    }

    if (this.boundPointerUp) {
      this.domElement.removeEventListener('pointerup', this.boundPointerUp as EventListener, true)
      this.domElement.removeEventListener(
        'pointercancel',
        this.boundPointerUp as EventListener,
        true
      )
      this.boundPointerUp = null
    }

    if (this.boundWheel) {
      this.domElement.removeEventListener('wheel', this.boundWheel as EventListener, true)
      this.boundWheel = null
    }

    if (this.boundDoubleClick) {
      this.domElement.removeEventListener('dblclick', this.boundDoubleClick as EventListener)
      this.boundDoubleClick = null
    }

    if (this.boundKeyDown) {
      this.domElement.removeEventListener('keydown', this.boundKeyDown as EventListener)
      this.boundKeyDown = null
    }

    if (this.boundKeyUp) {
      this.domElement.removeEventListener('keyup', this.boundKeyUp as EventListener)
      this.boundKeyUp = null
    }

    if (this.boundBlur) {
      this.domElement.removeEventListener('blur', this.boundBlur)
      this.boundBlur = null
    }

    this.activePivot = null
    this.activePointerId = null
    this.lastPointerScreen = null
    this.activeDragMode = null
    this.clearWheelZoomState()
    this.keyState.clear()
    this.keyboardCurrentSpeed = 0
  }

  private updateKeyboardTranslation(deltaTime?: number | null): boolean {
    if (!this.services || this.keyState.size === 0) {
      this.keyboardCurrentSpeed = 0
      return false
    }

    const safeDelta =
      deltaTime && Number.isFinite(deltaTime) && deltaTime > 0
        ? deltaTime
        : EarthController.DEFAULT_FRAME_DELTA
    const current = this.services.rig.getState()
    const forward = current.target.clone().sub(current.position)
    if (forward.lengthSq() <= 1e-12) {
      this.keyboardCurrentSpeed = 0
      return false
    }

    const yaw = this.getStableYaw(forward.normalize())
    const planarForward = new THREE.Vector3(0, 1, 0)
      .applyAxisAngle(EarthController.WORLD_UP, yaw)
      .normalize()
    const side = new THREE.Vector3(1, 0, 0)
      .applyAxisAngle(EarthController.WORLD_UP, yaw)
      .normalize()
    const movement = new THREE.Vector3()

    const moveForward = this.keyState.has('KeyW') || this.keyState.has('ArrowUp')
    const moveBackward = this.keyState.has('KeyS') || this.keyState.has('ArrowDown')
    const moveLeft = this.keyState.has('KeyA') || this.keyState.has('ArrowLeft')
    const moveRight = this.keyState.has('KeyD') || this.keyState.has('ArrowRight')
    const moveUp = this.keyState.has('KeyQ')
    const moveDown = this.keyState.has('KeyE')

    if (moveForward !== moveBackward) {
      movement.addScaledVector(planarForward, moveForward ? 1 : -1)
    }

    if (moveLeft !== moveRight) {
      movement.addScaledVector(side, moveRight ? 1 : -1)
    }

    if (moveUp !== moveDown) {
      movement.addScaledVector(EarthController.WORLD_UP, moveUp ? 1 : -1)
    }

    if (movement.lengthSq() <= 1e-12) {
      this.keyboardCurrentSpeed = 0
      return false
    }

    const frameSpeed = this.advanceKeyboardSpeed(safeDelta)
    movement.multiplyScalar(frameSpeed)

    const nextPosition = current.position.clone().add(movement)
    const nextTarget = current.target.clone().add(movement)
    this.commitRigState({
      ...current,
      position: nextPosition,
      target: nextTarget,
      up: current.up.clone(),
    })
    this.target.copy(nextTarget)
    this.cursor.add(movement)
    return true
  }

  private rotateAroundPivot(delta: THREE.Vector2, pivot: THREE.Vector3): void {
    if (!this.services) {
      return
    }

    const previous = this.services.rig.getState()
    const viewport = this.services.getViewportSize()
    const width = Math.max(viewport.width, 1)
    const height = Math.max(viewport.height, 1)
    const yawDelta = -(delta.x / width) * EarthController.LEGACY_ROTATION_SPEED * 0.5
    let pitchDelta = -(delta.y / height) * EarthController.LEGACY_ROTATION_SPEED * 0.2

    const forward = previous.target.clone().sub(previous.position).normalize()
    const yaw = this.getStableYaw(forward)
    const currentPitch = Math.atan2(
      forward.z,
      Math.sqrt(forward.x * forward.x + forward.y * forward.y)
    )
    const nextPitch = THREE.MathUtils.clamp(
      currentPitch + pitchDelta,
      -EarthController.MAX_ABSOLUTE_PITCH,
      EarthController.MAX_ABSOLUTE_PITCH
    )
    pitchDelta = nextPitch - currentPitch

    if (Math.abs(pitchDelta) <= 1e-8 && Math.abs(yawDelta) <= 1e-8) {
      return
    }

    const side = new THREE.Vector3(1, 0, 0)
      .applyAxisAngle(EarthController.WORLD_UP, yaw)
      .normalize()

    const pivotToCamera = previous.position.clone().sub(pivot)
    const pivotToTarget = previous.target.clone().sub(pivot)
    pivotToCamera.applyAxisAngle(side, pitchDelta)
    pivotToTarget.applyAxisAngle(side, pitchDelta)

    pivotToCamera.applyAxisAngle(EarthController.WORLD_UP, yawDelta)
    pivotToTarget.applyAxisAngle(EarthController.WORLD_UP, yawDelta)

    const nextPosition = pivot.clone().add(pivotToCamera)
    const nextTarget = pivot.clone().add(pivotToTarget)
    const nextForward = nextTarget.clone().sub(nextPosition).normalize()
    const nextYaw = yaw + yawDelta
    const nextSide = new THREE.Vector3(1, 0, 0)
      .applyAxisAngle(EarthController.WORLD_UP, nextYaw)
      .normalize()
    const nextUp = nextSide.clone().cross(nextForward).normalize()
    this.lastStableYaw = nextYaw

    this.commitRigState({
      ...previous,
      position: nextPosition,
      target: nextTarget,
      up: nextUp,
    })

    const camera = this.syncFromRig()
    this.target.copy(nextTarget)
    this.cursor.copy(pivot)
    camera.updateMatrixWorld(true)
    this.updatePivotIndicatorScale(camera)
  }

  private getStableYaw(forward: THREE.Vector3): number {
    const horizontalLength = Math.sqrt(forward.x * forward.x + forward.y * forward.y)

    if (horizontalLength > 1e-6) {
      this.lastStableYaw = Math.atan2(forward.y, forward.x) - Math.PI / 2
    }

    return this.lastStableYaw
  }

  private async beginActivationTransition(): Promise<void> {
    if (!this.services) {
      return
    }

    const state = this.services.rig.getState()
    const forward = state.target.clone().sub(state.position)
    if (forward.lengthSq() < 1e-8) {
      this.isActivating = false
      return
    }

    const desiredUp = this.computeEarthAlignedUp(forward.normalize())
    if (desiredUp.angleTo(state.up) <= EarthController.UP_ALIGNMENT_EPSILON) {
      this.isActivating = false
      return
    }

    const token = ++this.activationToken
    this.isActivating = true

    await this.services.rig.animateTo(
      {
        up: desiredUp,
      },
      EarthController.ACTIVATION_TRANSITION_MS
    )

    if (this.activationToken !== token || !this.enabled) {
      return
    }

    this.isActivating = false
    const camera = this.syncFromRig()
    this.target.copy(this.services.rig.target)
    this.cursor.copy(this.services.rig.target)
    this.resetKeyboardMotionProfile(state.position.distanceTo(state.target))
    this.rememberCurrentRigState()
    camera.updateMatrixWorld(true)
    this.updatePivotIndicatorScale(camera)
  }

  private computeEarthAlignedUp(forward: THREE.Vector3): THREE.Vector3 {
    const yaw = this.getStableYaw(forward)
    const side = new THREE.Vector3(1, 0, 0)
      .applyAxisAngle(EarthController.WORLD_UP, yaw)
      .normalize()
    return side.clone().cross(forward).normalize()
  }

  private panCamera(delta: THREE.Vector2): void {
    if (!this.services) {
      return
    }

    const previous = this.services.rig.getState()
    const camera = this.syncFromRig()
    camera.updateMatrixWorld(true)

    const viewport = this.services.getViewportSize()
    const width = Math.max(viewport.width, 1)
    const height = Math.max(viewport.height, 1)
    const side = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize()
    const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).normalize()

    let panX = 0
    let panY = 0

    if (camera instanceof THREE.PerspectiveCamera) {
      const panDepth = this.resolvePointerPanNavigationScale(
        previous.position.distanceTo(previous.target)
      )
      const targetDistance = panDepth * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2)
      panX = (-delta.x * targetDistance) / height
      panY = (delta.y * targetDistance) / height
    } else if (camera instanceof THREE.OrthographicCamera) {
      panX = (-delta.x * (camera.right - camera.left)) / camera.zoom / width
      panY = (delta.y * (camera.top - camera.bottom)) / camera.zoom / height
    }

    const pan = side.multiplyScalar(panX).add(up.multiplyScalar(panY))
    const nextPosition = previous.position.clone().add(pan)
    const nextTarget = previous.target.clone().add(pan)

    this.commitRigState({
      ...previous,
      position: nextPosition,
      target: nextTarget,
      up: previous.up.clone(),
    })

    this.target.copy(nextTarget)
    this.cursor.add(pan)
    const syncedCamera = this.syncFromRig()
    syncedCamera.updateMatrixWorld(true)
  }

  private updateLegacyWheelZoom(deltaTime?: number | null): boolean {
    if (!this.services) {
      return false
    }

    const camera = this.syncFromRig()
    if (!(camera instanceof THREE.PerspectiveCamera)) {
      this.clearWheelZoomState()
      return false
    }

    const safeDelta =
      deltaTime && Number.isFinite(deltaTime) && deltaTime > 0
        ? deltaTime
        : EarthController.DEFAULT_FRAME_DELTA
    const fade = 0.5 ** (EarthController.LEGACY_WHEEL_FADE_FACTOR * safeDelta)
    const progression = 1 - fade
    let changed = false

    const wheelPivotPending = this.wheelPivotPending
    if (this.wheelDelta !== 0 && !wheelPivotPending) {
      const current = this.services.rig.getState()
      const forward = current.target.clone().sub(current.position).normalize()
      const distanceToTarget = current.position.distanceTo(current.target)
      const navigationScale = this.resolveWheelNavigationScale(distanceToTarget)
      if (this.wheelPivot) {
        const step = resolvePickedPointWheelStep({
          position: current.position,
          target: current.target,
          forward,
          pickedPoint: this.wheelPivot,
          currentZoomDelta: this.zoomDelta,
          wheelDelta: this.wheelDelta,
          proportionalStepRatio: EarthController.LEGACY_WHEEL_STEP_RATIO,
          navigationScale,
          minStepRatio: EarthController.WHEEL_MIN_STEP_RATIO,
          absoluteMinStep: this.minZoomStep,
        })

        if (step.crossedPickedPoint) {
          this.zoomDelta.set(0, 0, 0)
          this.zoomDistance = null
          this.commitRigState({
            ...current,
            position: step.nextPosition,
            target: step.nextTarget,
            up: current.up.clone(),
          })
          changed = true
        } else {
          this.zoomDelta.copy(step.nextZoomDelta)
          this.zoomDistance = step.nextZoomDistance
        }
      } else {
        const moveDistance = resolveWheelForwardStep({
          cameraTargetDistance: distanceToTarget,
          wheelDelta: this.wheelDelta,
          proportionalStepRatio: EarthController.FREE_WHEEL_STEP_RATIO,
          navigationScale,
          minStepRatio: EarthController.WHEEL_MIN_STEP_RATIO,
          absoluteMinStep: this.minZoomStep,
        })
        const moveVector = forward.multiplyScalar(moveDistance)

        if (moveVector.lengthSq() > 1e-12) {
          this.zoomDelta.add(moveVector)
          changed = true
        }
        this.zoomDistance = null
      }
    }

    if (this.zoomDelta.lengthSq() > 1e-12) {
      const movement = this.zoomDelta.clone().multiplyScalar(progression)
      if (movement.lengthSq() > 1e-12) {
        const current = this.services.rig.getState()
        const forward = current.target.clone().sub(current.position).normalize()
        const nextPosition = current.position.clone().add(movement)
        const nextTarget =
          this.zoomDistance == null
            ? current.target.clone().add(movement)
            : nextPosition.clone().addScaledVector(forward, this.zoomDistance)
        this.commitRigState({
          ...current,
          position: nextPosition,
          target: nextTarget,
          up: current.up.clone(),
        })
        changed = true
      }
    }

    this.zoomDelta.multiplyScalar(fade)
    if (this.zoomDelta.lengthSq() <= 1e-12) {
      this.zoomDelta.set(0, 0, 0)
      this.zoomDistance = null
    }
    if (!wheelPivotPending) {
      this.wheelDelta = 0
      this.wheelPivot = null
    }

    if (changed) {
      this.target.copy(this.services.rig.target)
      const syncedCamera = this.syncFromRig()
      syncedCamera.updateMatrixWorld(true)
    }

    return changed
  }

  private updateOrthographicZoomAnimation(deltaTime?: number | null): boolean {
    if (
      !this.services ||
      this.orthographicZoomTarget == null ||
      !this.orthographicZoomAnchorClient
    ) {
      return false
    }

    const camera = this.syncFromRig()
    if (!(camera instanceof THREE.OrthographicCamera)) {
      this.clearOrthographicZoomAnimation()
      return false
    }

    const targetZoom = this.orthographicZoomTarget
    const currentZoom = camera.zoom
    const remaining = targetZoom - currentZoom
    if (remaining <= 1e-9) {
      this.clearOrthographicZoomAnimation()
      return false
    }

    const safeDelta =
      deltaTime && Number.isFinite(deltaTime) && deltaTime > 0
        ? deltaTime
        : EarthController.DEFAULT_FRAME_DELTA
    const fade = 0.5 ** (EarthController.LEGACY_WHEEL_FADE_FACTOR * safeDelta)
    const nextZoom = Math.min(targetZoom, currentZoom + remaining * (1 - fade))
    if (
      !this.applyOrthographicZoomAtClientPoint(
        camera,
        this.orthographicZoomAnchorClient.x,
        this.orthographicZoomAnchorClient.y,
        nextZoom
      )
    ) {
      this.clearOrthographicZoomAnimation()
      return false
    }

    if (targetZoom - camera.zoom <= 1e-9) {
      this.clearOrthographicZoomAnimation()
    }

    this.target.copy(this.services.rig.target)
    this.rememberCurrentRigState()
    return true
  }

  private applyOrthographicZoomAtClientPoint(
    camera: THREE.OrthographicCamera,
    clientX: number,
    clientY: number,
    zoom: number
  ): boolean {
    if (!this.domElement || !this.services) {
      return false
    }

    const rect = this.domElement.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) {
      return false
    }

    const mouseX = ((clientX - rect.left) / rect.width) * 2 - 1
    const mouseY = -((clientY - rect.top) / rect.height) * 2 + 1
    const mouseBefore = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera)
    const previousZoom = camera.zoom

    camera.zoom = Math.max(1e-9, zoom)
    if (Math.abs(camera.zoom - previousZoom) <= 1e-9) {
      return false
    }

    camera.updateProjectionMatrix()
    const mouseAfter = new THREE.Vector3(mouseX, mouseY, 0).unproject(camera)
    const translation = mouseBefore.sub(mouseAfter)
    camera.position.add(translation)
    camera.updateMatrixWorld(true)
    this.target.add(translation)
    syncRigFromCamera(this.services, camera, this.target)
    return true
  }

  private startOrthographicDoubleClickZoom(
    event: MouseEvent,
    camera: THREE.OrthographicCamera,
    hit: NativeControlSurfaceQueryHit
  ): boolean {
    const current = this.services!.rig.getState()
    const navigationScale = this.resolveWheelNavigationScale(
      current.position.distanceTo(current.target)
    )
    const targetRadius = resolveDoubleClickTargetRadius(
      hit,
      navigationScale,
      EarthController.WHEEL_MIN_STEP_RATIO,
      EarthController.WHEEL_ABSOLUTE_MIN_STEP
    )
    const targetHalfHeight = Math.max(
      targetRadius / Math.tan(THREE.MathUtils.degToRad(current.fov) / 2),
      1e-9
    )
    const targetZoom = Math.max(
      camera.top / targetHalfHeight,
      camera.zoom * EarthController.DOUBLE_CLICK_ORTHOGRAPHIC_MIN_ZOOM_FACTOR
    )
    if (targetZoom <= camera.zoom + 1e-9) {
      return false
    }

    this.orthographicZoomTarget = targetZoom
    this.orthographicZoomAnchorClient = new THREE.Vector2(event.clientX, event.clientY)
    return true
  }

  private clearOrthographicZoomAnimation(): void {
    this.orthographicZoomTarget = null
    this.orthographicZoomAnchorClient = null
  }

  private clearWheelZoomState(): void {
    this.wheelDelta = 0
    this.zoomDelta.set(0, 0, 0)
    this.wheelPivot = null
    this.wheelPivotPending = false
    this.zoomDistance = null
    this.clearOrthographicZoomAnimation()
    this.resetWheelPickCache()
  }

  private resetKeyboardMotionProfile(distance?: number): void {
    this.keyboardMaxSpeed = this.resolveKeyboardMaxSpeed(distance)
    this.keyboardCurrentSpeed = 0
  }

  private advanceKeyboardSpeed(deltaTime: number): number {
    const acceleration = this.keyboardMaxSpeed / EarthController.KEYBOARD_ACCELERATION_DURATION
    this.keyboardCurrentSpeed = Math.min(
      this.keyboardMaxSpeed,
      this.keyboardCurrentSpeed + acceleration * deltaTime
    )
    return this.keyboardCurrentSpeed * deltaTime
  }

  private resolveKeyboardMaxSpeed(distance?: number): number {
    return this.resolveKeyboardNavigationScale(distance ?? this.getCurrentRigDistance())
  }

  private getCurrentRigDistance(): number {
    if (!this.services) {
      return EarthController.MIN_KEYBOARD_MAX_SPEED
    }

    const current = this.services.rig.getState()
    return current.position.distanceTo(current.target)
  }

  private resolveWheelNavigationScale(
    distance: number,
    absoluteMinimum = EarthController.NAVIGATION_SCALE_ABSOLUTE_MINIMUM
  ): number {
    return resolveNavigationScale({
      cameraTargetDistance: distance,
      sceneBounds: null,
      absoluteMinimum,
    })
  }

  private resolveKeyboardNavigationScale(distance: number): number {
    return resolveNavigationScale({
      cameraTargetDistance: distance,
      sceneBounds: null,
      absoluteMinimum: EarthController.MIN_KEYBOARD_MAX_SPEED,
    })
  }

  private resolvePointerPanNavigationScale(
    distance: number,
    absoluteMinimum = EarthController.NAVIGATION_SCALE_ABSOLUTE_MINIMUM
  ): number {
    return resolveNavigationScale({
      cameraTargetDistance: distance,
      sceneBounds: null,
      absoluteMinimum,
    })
  }

  private commitRigState(state: CameraState): void {
    if (!this.services) {
      return
    }

    this.services.rig.setState(state)
    this.lastCommittedRigState = this.cloneCameraState(state)
  }

  private rememberCurrentRigState(): void {
    if (!this.services) {
      return
    }

    this.lastCommittedRigState = this.cloneCameraState(this.services.rig.getState())
  }

  private resetKeyboardMotionProfileForExternalRigChange(): void {
    if (!this.services) {
      return
    }

    const current = this.services.rig.getState()
    if (!this.lastCommittedRigState) {
      this.lastCommittedRigState = this.cloneCameraState(current)
      return
    }

    if (this.cameraStateEquals(this.lastCommittedRigState, current)) {
      return
    }

    if (
      this.keyState.size === 0 &&
      this.activeDragMode === null &&
      this.wheelDelta === 0 &&
      this.zoomDelta.lengthSq() <= 1e-12
    ) {
      this.resetKeyboardMotionProfile(current.position.distanceTo(current.target))
    }
    this.lastCommittedRigState = this.cloneCameraState(current)
  }

  private cloneCameraState(state: CameraState): CameraState {
    return {
      ...state,
      position: state.position.clone(),
      target: state.target.clone(),
      up: state.up.clone(),
    }
  }

  private cameraStateEquals(a: CameraState, b: CameraState): boolean {
    return (
      a.mode === b.mode &&
      Math.abs(a.fov - b.fov) <= 1e-9 &&
      Math.abs(a.zoom - b.zoom) <= 1e-9 &&
      a.position.distanceToSquared(b.position) <= 1e-18 &&
      a.target.distanceToSquared(b.target) <= 1e-18 &&
      a.up.distanceToSquared(b.up) <= 1e-18
    )
  }

  private showPivotIndicator(point: THREE.Vector3): void {
    this.pivotIndicator.position.copy(point)
    this.pivotIndicator.visible = true
  }

  private hidePivotIndicator(): void {
    this.pivotIndicator.visible = false
  }

  private updatePivotIndicatorScale(
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera
  ): void {
    if (!this.pivotIndicator.visible) {
      return
    }

    const viewportHeight = Math.max(this.services?.getViewportSize().height ?? 0, 1)
    const distance = Math.max(camera.position.distanceTo(this.pivotIndicator.position), 1e-6)
    const radius = this.projectedRadius(1, camera, distance, viewportHeight)
    const scale = radius <= 1e-6 ? 1 : EarthController.PIVOT_INDICATOR_PIXEL_RADIUS / radius
    this.pivotIndicator.scale.setScalar(scale)
  }

  private projectedRadius(
    radius: number,
    camera: THREE.Camera,
    distance: number,
    viewportHeight: number
  ): number {
    if (camera instanceof THREE.PerspectiveCamera) {
      const fov = THREE.MathUtils.degToRad(camera.fov)
      return (viewportHeight * radius) / (2 * distance * Math.tan(fov / 2))
    }

    if (camera instanceof THREE.OrthographicCamera) {
      return (viewportHeight * radius) / Math.max(camera.top - camera.bottom, 1e-6)
    }

    return 1
  }

  /** 从事件坐标拾取 pivot 并更新 cursor/target */
  private updatePivotFromEvent(
    event: PointerEvent | WheelEvent,
    interactionType: 'click' | 'move',
    updateTarget: boolean
  ): void {
    const pivot = this.pickPivot(event, interactionType)
    if (!pivot) {
      return
    }

    this.cursor.copy(pivot)

    if (updateTarget) {
      this.target.copy(pivot)
      if (this.services) {
        const camera = this.object as THREE.PerspectiveCamera | THREE.OrthographicCamera
        camera.updateMatrixWorld(true)
        syncRigFromCamera(this.services, camera, this.target)
        this.rememberCurrentRigState()
      }
    }
  }

  private pickPivot(
    event: MouseEvent | PointerEvent | WheelEvent,
    interactionType: 'click' | 'move'
  ): THREE.Vector3 | null {
    return this.pickSurfaceHit(event, interactionType)?.point.clone() ?? null
  }

  private pickSurfaceHit(
    event: MouseEvent | PointerEvent | WheelEvent,
    interactionType: 'click' | 'move'
  ): NativeControlSurfaceQueryHit | null {
    if (!this.services?.surfaceQuery || !this.domElement) {
      return null
    }

    const screen = new THREE.Vector2(event.clientX, event.clientY)
    const camera = this.syncFromRig()
    const minDistance = this.resolveSurfacePickMinDistance(screen, camera)
    if (minDistance === null) {
      return null
    }
    return this.services.surfaceQuery.queryAtScreen(screen, camera, {
      kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
      interactionType,
      interactionSource: 'earth',
      windowSize: this.surfacePickWindowSize,
      centerSphereRadius:
        interactionType === 'move' ? EarthController.GSPLAT_HOVER_RADIUS : undefined,
      ...(minDistance === undefined ? {} : { minDistance }),
    })
  }

  private pickSurfaceHitAsync(
    event: MouseEvent | PointerEvent | WheelEvent,
    interactionType: 'click' | 'move',
    signal: AbortSignal,
    camera?: THREE.Camera
  ): Promise<NativeControlSurfaceQueryHit | null> {
    if (!this.services?.surfaceQuery?.queryAtScreenAsync || !this.domElement) {
      return Promise.resolve(null)
    }

    const screen = new THREE.Vector2(event.clientX, event.clientY)
    const requestCamera = camera ?? this.syncFromRig()
    const minDistance = this.resolveSurfacePickMinDistance(screen, requestCamera)
    if (minDistance === null) {
      return Promise.resolve(null)
    }
    return this.services.surfaceQuery.queryAtScreenAsync(screen, requestCamera, {
      kinds: [...NATIVE_CONTROL_SURFACE_KINDS],
      interactionType,
      interactionSource: 'earth',
      windowSize: this.surfacePickWindowSize,
      centerSphereRadius:
        interactionType === 'move' ? EarthController.GSPLAT_HOVER_RADIUS : undefined,
      signal,
      ...(minDistance === undefined ? {} : { minDistance }),
    })
  }

  private resolveSurfacePickMinDistance(
    screen: THREE.Vector2,
    camera: THREE.Camera
  ): number | null | undefined {
    if (!this.domElement) {
      return this.surfacePickNearOffset === undefined ? undefined : null
    }
    const rect = this.domElement.getBoundingClientRect()
    return resolveNativeControlPickMinDistanceAtScreen(
      camera,
      screen,
      { x: rect.left, y: rect.top, width: rect.width, height: rect.height },
      this.surfacePickNearOffset
    )
  }

  private schedulePointerPivotPick(event: PointerEvent): void {
    if (!this.services) return
    const version = ++this.pickRequestVersion
    const requestCamera = this.syncFromRig()
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    void this.pickSurfaceHitAsync(event, 'move', controller.signal, requestCamera)
      .then((hit) => {
        if (
          !hit ||
          version !== this.pickRequestVersion ||
          this.activePointerId !== event.pointerId ||
          this.activeDragMode !== 'rotate' ||
          !isCurrentViewportCamera(this.services!, requestCamera)
        ) {
          return
        }
        const pivot = hit.point.clone()
        this.activePivot = pivot
        this.cursor.copy(pivot)
        this.showPivotIndicator(pivot)
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
        }
      })
  }

  private scheduleDoubleClickPick(event: MouseEvent, camera: THREE.Camera): void {
    const version = ++this.pickRequestVersion
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    void this.pickSurfaceHitAsync(event, 'click', controller.signal, camera)
      .then((hit) => {
        if (
          hit &&
          version === this.pickRequestVersion &&
          this.enabled &&
          isCurrentViewportCamera(this.services!, camera)
        ) {
          this.applyDoubleClickHit(event, camera, hit)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
        }
      })
  }

  private applyDoubleClickHit(
    event: MouseEvent,
    camera: THREE.Camera,
    hit: NativeControlSurfaceQueryHit
  ): void {
    if (camera instanceof THREE.OrthographicCamera) {
      this.clearWheelZoomState()
      if (!this.startOrthographicDoubleClickZoom(event, camera, hit)) {
        return
      }

      if (!this.updateOrthographicZoomAnimation(EarthController.DEFAULT_FRAME_DELTA)) {
        return
      }

      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }

    if (!(camera instanceof THREE.PerspectiveCamera)) {
      return
    }

    const pivot = hit.point
    const current = this.services!.rig.getState()
    const navigationScale = this.resolveWheelNavigationScale(
      current.position.distanceTo(current.target)
    )
    const wheelDelta = resolveDoubleClickWheelDelta({
      position: current.position,
      pickedPoint: pivot,
      hit,
      proportionalStepRatio: EarthController.LEGACY_WHEEL_STEP_RATIO,
      navigationScale,
      minStepRatio: EarthController.WHEEL_MIN_STEP_RATIO,
      absoluteMinStep: this.minZoomStep,
    })
    if (wheelDelta <= 0) {
      return
    }

    this.cursor.copy(pivot)
    this.wheelPivot = pivot.clone()
    this.wheelDelta += wheelDelta
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  private getWheelPivot(event: WheelEvent): THREE.Vector3 | null {
    const reusablePivot = this.getReusableWheelPivot(event)
    if (reusablePivot !== undefined) {
      return reusablePivot
    }

    // Do not abort an in-flight GPU pivot pick on every wheel event.  The coordinator already
    // coalesces move requests; keeping this request alive avoids wasting the submitted compute
    // and readback work while the current wheel delta continues to accumulate.
    if (this.wheelPivotPending) {
      return null
    }

    if (shouldUseAsyncPicking(this.services)) {
      this.scheduleWheelPivotPick(event)
      return null
    }

    const pivot = this.pickPivot(event, 'move')
    this.cacheWheelPivot(event, pivot)
    return pivot
  }

  private getReusableWheelPivot(event: WheelEvent): THREE.Vector3 | null | undefined {
    if (
      !this.lastWheelPickScreen ||
      performance.now() - this.lastWheelPickAt >
        (this.lastWheelPickedPivot
          ? EarthController.WHEEL_GESTURE_IDLE_MS
          : EarthController.WHEEL_PICK_REUSE_MS)
    ) {
      return undefined
    }

    const deltaX = event.clientX - this.lastWheelPickScreen.x
    const deltaY = event.clientY - this.lastWheelPickScreen.y
    const maxDistance = EarthController.WHEEL_PICK_REUSE_DISTANCE_PX
    if (deltaX * deltaX + deltaY * deltaY > maxDistance * maxDistance) {
      return undefined
    }

    // Extend the reuse window on every wheel event.  Trackpads and high-resolution wheels can
    // emit events more than the reuse window apart while still belonging to one gesture; without refreshing
    // this timestamp, the next event starts another full GPU pick (cull + projector + radix +
    // readback) even though the pointer anchor has not changed.
    if (this.lastWheelPickedPivot) {
      const camera = this.syncFromRig()
      if (
        !isPointBeyondNativeControlPickPlane(
          camera,
          this.lastWheelPickedPivot,
          this.surfacePickNearOffset
        )
      ) {
        this.resetWheelPickCache()
        return undefined
      }
      this.lastWheelPickAt = performance.now()
    }
    return this.lastWheelPickedPivot?.clone() ?? null
  }

  private cacheWheelPivot(event: WheelEvent, pivot: THREE.Vector3 | null): void {
    if (!this.lastWheelPickScreen) {
      this.lastWheelPickScreen = new THREE.Vector2()
    }

    this.lastWheelPickScreen.set(event.clientX, event.clientY)
    this.lastWheelPickAt = performance.now()
    this.lastWheelPickedPivot = pivot?.clone() ?? null
  }

  private resetWheelPickCache(): void {
    this.lastWheelPickAt = -Infinity
    this.lastWheelPickedPivot = null
    this.lastWheelPickScreen = null
  }

  private scheduleWheelPivotPick(event: WheelEvent): void {
    if (this.wheelPivotPending) return
    const version = ++this.pickRequestVersion
    const requestCamera = this.syncFromRig()
    this.pickAbortController?.abort()
    const controller = new AbortController()
    this.pickAbortController = controller
    this.wheelPivotPending = true
    void this.pickSurfaceHitAsync(event, 'move', controller.signal, requestCamera)
      .then((hit) => {
        if (version !== this.pickRequestVersion || !this.wheelPivotPending || !this.enabled) {
          return
        }
        if (!isCurrentViewportCamera(this.services!, requestCamera)) {
          this.wheelPivotPending = false
          this.wheelPivot = null
          return
        }
        const pivot = hit?.point.clone() ?? null
        this.cacheWheelPivot(event, pivot)
        this.wheelPivotPending = false
        if (pivot) {
          this.cursor.copy(pivot)
          this.wheelPivot = pivot.clone()
        }
      })
      .catch(() => {
        if (version !== this.pickRequestVersion || !this.wheelPivotPending || !this.enabled) {
          return
        }
        if (!isCurrentViewportCamera(this.services!, requestCamera)) {
          this.wheelPivotPending = false
          this.wheelPivot = null
          return
        }
        this.wheelPivotPending = false
        this.wheelPivot = null
      })
      .finally(() => {
        if (this.pickAbortController === controller) {
          this.pickAbortController = null
        }
      })
  }

  private getFallbackPivot(): THREE.Vector3 {
    const camera = this.syncFromRig()
    const width = Math.max(this.domElement?.clientWidth ?? 1, 1)
    const height = Math.max(this.domElement?.clientHeight ?? 1, 1)
    const ndc = new THREE.Vector3(0, 0, 0.78)
    ndc.x = ((width * 0.5) / width) * 2 - 1
    ndc.y = -(((height * 0.5) / height) * 2 - 1)
    return ndc.unproject(camera)
  }

  private cancelPendingPickRequests(): void {
    this.pickRequestVersion += 1
    this.pickAbortController?.abort()
    this.pickAbortController = null
    this.wheelPivotPending = false
  }
}
